<template>
  <div class="w-full font-proxima">
    <div class="overflow-x-auto shadow-sm ring-1 ring-black ring-opacity-5 rounded-sm bg-white">
      <table class="min-w-full divide-y divide-gray-300">
        <thead class="bg-red-50">
          <tr>
            <th v-for="header in HEADERS" :key="header" class="p-2 text-left align-middle text-sm font-bold border border-white text-blue-500 whitespace-nowrap">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <tr v-for="(row, index) in rows" :key="row.id" :class="index % 2 === 0 ? undefined : 'bg-gray-100'">
            <td class="px-3 py-2 text-sm whitespace-nowrap">
              <button type="button" class="text-blue-500 hover:text-blue-600" @click="openCase(row)">
                {{ row.formattedAssistanceRequestId ?? generateAssistanceRequestId(row.idPublic) }}
              </button>
            </td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ row.name ?? [row.firstName, row.lastName].filter(Boolean).join(' ') }}</td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ friendlyStatus(row.status) }}</td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ row.assistanceTypeDescription ?? row.assistanceType }}</td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ money(row.amountRequested) }}</td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ row.serviceAreaName }}</td>
            <td class="px-3 py-2 text-sm text-black whitespace-nowrap">{{ displayDate(row.createdAt ?? row.createdDate) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="mt-1 text-xs text-gray-500">
      Showing {{ rows.length.toLocaleString() }} of {{ (output?.count ?? rows.length).toLocaleString() }} matching assistance requests
    </p>
  </div>
</template>
<script setup lang="ts">
import { EnumAssistanceRequestStatus, EnumAssistanceRequestStatusUserFriendlyJson } from '~/composables/Enums';

interface CaseRow {
  id: string;
  idPublic?: number;
  formattedAssistanceRequestId?: string;
  applicationId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  status?: string;
  assistanceType?: string;
  assistanceTypeDescription?: string;
  serviceAreaName?: string;
  amountRequested?: number;
  createdAt?: string;
  createdDate?: string;
}

const props = defineProps<{ part: { output?: { count?: number; cases?: CaseRow[] } } }>();

const HEADERS = ['AR ID', 'Applicant', 'Status', 'Type', 'Requested', 'Service Area', 'Created'];

const userStore = useUserStore();

const output = computed(() => props.part.output);
const rows = computed<CaseRow[]>(() => output.value?.cases ?? []);

const friendlyStatus = (status?: string) => (status ? (EnumAssistanceRequestStatusUserFriendlyJson[status] ?? status) : '');
const money = (cents?: number) => (cents == null ? '' : formatNumberToMoney(convertFromCents(cents)));
const displayDate = (value?: string) => (value ? new Date(value).toLocaleDateString() : '');

// Same navigation + permission gate as the Applications list page (pages/applications/index.vue)
const openCase = (row: CaseRow) => {
  if (!row.applicationId || !row.status) return;
  if (!userStore.canEdit(row.status as EnumAssistanceRequestStatus)) {
    toastWarning('Warning', 'You do not have permission to perform this action.');
    return;
  }
  window.open(`/applications/${row.applicationId}/show?arId=${row.id}`, '_blank');
};
</script>
